// ── 통합 메뉴 출퇴근 관리 탭 화면 (2026-09-30 "통합에 있는 출퇴근 관리도 똑같이 — 합치는 것부터 디자인까지") ──
//   본사·회사별과 같은 탭 화면(AttendanceHub)을 통합 메뉴 주소로 그린다. 옛 IntegratedAttendance(상세)·
//   IntegratedAttendanceCalendar(달력) 두 화면을 대신한다 — 각 탭에 넘기는 API 주소·회사 칩 값은 옛 두 화면 그대로.
//     출퇴근 메뉴      → [달력][직원별 상세], 첫 탭 달력, ☰ = 예외 스케줄 등록 → 시간 설정 → 엑셀 다운로드 → IP 관리
//     출퇴근 달력 메뉴 → [달력]만(☰ = 예외 스케줄 등록). 같은 회사 묶음의 출퇴근 메뉴가 내 목록에 있으면 그 달력 탭으로 넘긴다.
//   회사 칩 선택은 두 탭이 한 벌을 같이 쓴다 — 탭을 옮겨도 그대로, 숨겨 둔 달력도 칩이 바뀌면 다시 불러온다.
import React, { useCallback, useState } from 'react';
import { Navigate } from 'react-router-dom';
import AttendanceManagement from '../AttendanceManagement';
import AttendanceCalendar from '../AttendanceCalendar';
import AttendanceHub from './AttendanceHub';
import { integratedAttendanceTabs, findPairedAttendanceMenu } from './integratedAttendanceTabs';

/**
 * @param {object}   menu   이 통합 메뉴 { id, name, feature_type: 'attendance'|'calendar', company_codes }
 * @param {object[]} menus  내가 열 수 있는 통합 메뉴 목록(/api/my/integrated-menus) — 달력 메뉴의 짝 찾기용
 */
export default function IntegratedAttendanceHub({ menu, menus }) {
  const paired = findPairedAttendanceMenu(menu, menus);
  if (paired) return <Navigate to={`/admin/integrated/${paired.id}?view=calendar`} replace />;
  return <IntegratedAttendanceTabsView key={menu.id} menu={menu} />;
}

function IntegratedAttendanceTabsView({ menu }) {
  const menuId = menu.id;
  const companyCodes = Array.isArray(menu.company_codes) ? menu.company_codes : [];
  // 기본: 전부 켬 (옛 두 화면과 같다). 칩 하나는 늘 켜져 있다(CompanyFilterChips).
  const [selected, setSelected] = useState(() => [...companyCodes]);
  const timeSettingsPathBuilder = useCallback((code = 'PINE') => {
    const safeCode = encodeURIComponent(String(code || 'PINE').toUpperCase());
    return `/api/admin/integrated-menus/${menuId}/attendance/settings?codes=${safeCode}`;
  }, [menuId]);

  const attendanceBase = `/api/admin/integrated-menus/${menuId}/attendance`;
  const calendarBase = `/api/admin/integrated-menus/${menuId}/attendance-calendar`;
  const chips = { companyCodes, selectedCompanyCodes: selected, setSelectedCompanyCodes: setSelected };
  // 달력 메뉴(탭 하나)는 제목도 예전 그대로 "출퇴근 달력" — 사이드바에서 누른 메뉴와 같은 이름이 보이게.
  const calendarOnly = menu.feature_type === 'calendar';
  // ☰ 'IP 관리'(2026-10-23) — 출퇴근 메뉴만(달력 메뉴는 시간 설정·엑셀도 없다). 묶인 회사 전부 + 공용 IP.
  const ipScope = calendarOnly ? null : { apiBase: `/api/admin/ip-scope/menu/${menuId}`, allowShared: true };

  return (
    <AttendanceHub
      tabs={integratedAttendanceTabs(menu.feature_type)}
      renderCalendar={(p) => (
        <AttendanceCalendar
          calendarApiBase={calendarBase}
          {...chips}
          title={calendarOnly ? undefined : p.title}
          subtitle={calendarOnly ? undefined : p.subtitle}
          tabSlot={p.tabSlot} onPickPerson={p.onPickPerson}
          // 시간 설정·엑셀은 상세 탭과 같은 값으로 — companyCodes 를 꼭 넘긴다(빠지면 시간 설정 회사 목록이 PINE 하나로 뜬다)
          tools={p.withTools ? {
            attendancePath: attendanceBase, companyCode: null, apiBasePath: attendanceBase,
            companyCodes, selectedCompanyCodes: selected, timeSettingsPathBuilder,
          } : null}
          reloadSignal={p.reloadSignal}
          ipScope={ipScope}
        />
      )}
      renderDetail={(p) => (
        <AttendanceManagement
          attendancePath={attendanceBase} companyCode={null} apiBasePath={attendanceBase}
          {...chips} timeSettingsPathBuilder={timeSettingsPathBuilder}
          pageTitle={p.title} subtitle={p.subtitle} tabSlot={p.tabSlot}
          initialUserId={p.initialUserId} initialYm={p.initialYm}
          overrideApiBase={calendarBase} onDataChanged={p.onDataChanged}
          ipScope={ipScope}
        />
      )}
    />
  );
}
